import { useRef } from "react";

interface PerspectiveBar { label: string; verdict: string; }
interface AIDecisionCardProps {
  stockName: string; stockCode: string; action: string;
  actionType: "buy" | "sell" | "hold";
  confidence: number; suggestedPosition: number; reason: string;
  perspectives: PerspectiveBar[];
  onSwipe?: (direction: "left" | "right") => void;
  onClick?: () => void;
}

const actionStyles: Record<string, { bg: string; text: string }> = {
  buy: { bg: "bg-[#FEE2E2]", text: "text-[#E11D48]" },
  sell: { bg: "bg-[#D1FAE5]", text: "text-[#10B981]" },
  hold: { bg: "bg-[#FEF3C7]", text: "text-[#D97706]" },
};

function verdictColor(v: string): string {
  if (/超配|强买|多头|看多|买入|乐观|利好|积极|景气/.test(v)) return "#E11D48";
  if (/低配|空头|看空|卖出|减持|悲观|利空/.test(v)) return "#10B981";
  return "#64748B";
}

export default function AIDecisionCard({ stockName, stockCode, action, actionType, confidence, suggestedPosition, reason, perspectives, onSwipe, onClick }: AIDecisionCardProps) {
  const style = actionStyles[actionType];
  const touchStartX = useRef<number | null>(null);
  const swipedRef = useRef(false);
  const handleTouchStart = (e: React.TouchEvent) => { touchStartX.current = e.touches[0].clientX; swipedRef.current = false; };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) > 40) { swipedRef.current = true; onSwipe?.(dx < 0 ? "left" : "right"); }
  };
  const handleClick = () => { if (swipedRef.current) { swipedRef.current = false; return; } onClick?.(); };

  return (
    <div className="px-4 mt-3 cursor-pointer" onClick={handleClick} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      <div className="bg-white rounded-card p-4 border-l-[3px] border-l-primary-500">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${style.bg} ${style.text}`}>{action}</span>
            <span className="text-sm font-semibold text-foreground-950">{stockName}</span>
            <span className="text-xs text-foreground-400">{stockCode}</span>
          </div>
          <span className="text-[10px] text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded whitespace-nowrap">模拟 · 仅供参考</span>
        </div>
        <p className="text-xs text-foreground-600 mt-2 leading-relaxed">{reason}</p>
        <div className="grid grid-cols-4 gap-1.5 mt-3">
          {perspectives.map((p) => (
            <div key={p.label} className="text-center">
              <div className="text-[10px] text-foreground-500 mb-1">{p.label}</div>
              <div className="text-[11px] font-semibold leading-tight truncate" style={{ color: p.verdict ? verdictColor(p.verdict) : undefined }}>{p.verdict || "—"}</div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-100">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-foreground-500">置信度</span>
            <span className="text-xs font-semibold text-foreground-950">{"★".repeat(confidence)}{"☆".repeat(5 - confidence)}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-foreground-500">建议仓位</span>
            <span className="text-xs font-semibold text-primary-500">{suggestedPosition}%</span>
          </div>
          <span className="text-[10px] text-primary-500">查看详情 →</span>
        </div>
      </div>
    </div>
  );
}
